<template>
  <div class="singer-carousel-wrapper">
    <!-- 3D 轮播容器 -->
    <div v-if="singers.length > 0" class="shell" :style="shellStyle"
      @touchstart="onDragStart"
      @touchmove.prevent="onDragMove"
      @touchend="onDragEnd"
      @mousedown="onDragStart"
    >
      <div class="content" ref="carouselContent" :style="contentStyle">
        <div
          v-for="(singer, index) in singers"
          :key="singer.id"
          class="item"
          :style="getItemStyle(index)"
        >
          <div class="card-inner">
            <!-- 上半部分：头像(带猫耳) + 喇叭 -->
            <div class="card-top-row">
              <div class="avatar-halo"></div>
              <div class="avatar-wrap">
                <span class="cat-ear left"></span>
                <span class="cat-ear right"></span>
                <div class="card-avatar">
                  <img v-if="singer.avatar" :src="singer.avatar" :alt="singer.name" />
                  <div v-else class="card-avatar-placeholder">{{ singer.name.charAt(0) }}</div>
                </div>
              </div>
              <a
                v-if="singer.homepage"
                class="card-speaker"
                :href="singer.homepage"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg class="speaker-icon" viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>
                <span class="speaker-text">点我进主页</span>
              </a>
            </div>

            <!-- 名字 -->
            <div class="card-name">
              <span class="name-text">{{ singer.name }}</span>
            </div>

            <!-- 下半部分：左标签 + 右音符按钮 -->
            <div class="card-body">
              <div class="card-tags-col">
                <div
                  v-for="(t, ti) in getAllTags(singer)"
                  :key="ti"
                  class="trapezoid-tag"
                  :class="t.type"
                >{{ t.text }}</div>
              </div>
              <div class="card-action-col">
                <div class="music-note">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                </div>
                <button class="enter-songlist-btn" @click.stop="selectSinger(singer)">进入歌单</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 导航指示器 -->
    <div v-if="singers.length > 1" class="carousel-indicators">
      <button
        v-for="(singer, index) in singers"
        :key="singer.id"
        class="indicator-dot"
        :class="{ active: currentIndex === index }"
        @click="goTo(index)"
      ></button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SingerCarousel',
  props: {
    singers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentIndex: 0,
      currentAngle: 0,
      _isTransitioning: false,
      // 拖拽相关
      _dragStartX: 0,
      _dragStartAngle: 0,
      _dragging: false,
      _touchMoved: false,
    };
  },
  computed: {
    itemCount() {
      return this.singers.length;
    },
    cardWidth() {
      const w = window.innerWidth;
      if (w <= 480) return 130;
      if (w <= 768) return 150;
      return 190;
    },
    cardHeight() {
      return Math.round(this.cardWidth * 1.9);
    },
    radius() {
      if (this.itemCount <= 1) return Math.round(this.cardWidth * 1.22);
      const gapMargin = 1.2;
      const required = (this.cardWidth * gapMargin) / (2 * Math.sin(Math.PI / this.itemCount));
      return Math.ceil(Math.max(this.cardWidth * 1.45, required));
    },
    shellHeight() {
      const w = window.innerWidth;
      if (w <= 480) return 260;
      if (w <= 768) return 300;
      return 380;
    },
    shellPerspective() {
      const w = window.innerWidth;
      if (w <= 480) return 700;
      if (w <= 768) return 900;
      return 1200;
    },
    shellStyle() {
      return {
        '--card-width': this.cardWidth + 'px',
        '--card-height': this.cardHeight + 'px',
        '--avatar-size': Math.round(this.cardWidth * 0.34) + 'px',
        '--name-font': (this.cardWidth >= 200 ? 16 : this.cardWidth >= 180 ? 14 : 12) + 'px',
        '--tag-font': (this.cardWidth >= 200 ? 11 : 10) + 'px',
        '--btn-font': (this.cardWidth >= 200 ? 12 : 11) + 'px',
        height: this.shellHeight + 'px',
        perspective: this.shellPerspective + 'px',
      };
    },
    contentTransform() {
      return `translateZ(-${this.radius}px) rotateY(${this.currentAngle}deg)`;
    },
    contentTransition() {
      if (this.itemCount <= 1 || this._dragging) return 'none';
      return 'transform 0.6s cubic-bezier(0.77, 0, 0.175, 1)';
    },
    contentStyle() {
      return {
        transform: this.contentTransform,
        transition: this.contentTransition,
      };
    },
  },
  methods: {
    getItemStyle(index) {
      const angle = (index * 360) / this.itemCount;
      return {
        transform: `rotateY(${angle}deg) translateZ(${this.radius}px)`,
      };
    },
    getAllTags(singer) {
      const genre = this.splitTags(singer.genre);
      const timbre = this.splitTags(singer.timbre);
      const maxTags = 3;
      const tags = [];
      // 交替取 genre 与 timbre，避免单一类型占满所有标签位
      const len = Math.max(genre.length, timbre.length);
      for (let i = 0; i < len && tags.length < maxTags; i++) {
        if (i < genre.length) tags.push({ text: genre[i], type: 'tag-genre' });
        if (tags.length < maxTags && i < timbre.length) {
          tags.push({ text: timbre[i], type: 'tag-timbre' });
        }
      }
      return tags;
    },
    selectSinger(singer) {
      this.$emit('select-singer', singer);
    },
    goTo(index) {
      if (this._isTransitioning) return;
      this._isTransitioning = true;
      this.currentAngle = -index * (360 / this.itemCount);
      this.currentIndex = index;
      setTimeout(() => {
        this._isTransitioning = false;
      }, 600);
    },
    splitTags(str) {
      if (!str) return [];
      return str.split(/[,，、/]+/).map((t) => t.trim()).filter(Boolean);
    },
    onDragStart(e) {
      if (this.itemCount <= 1) return;
      if (e.button && e.button !== 0) return;
      this._isTransitioning = false;
      this._dragging = true;
      this._touchMoved = false;
      const pos = e.touches ? e.touches[0] : e;
      this._dragStartX = pos.clientX;
      this._dragStartAngle = this.currentAngle;
      const el = this.$refs.carouselContent;
      if (el) {
        el.style.transition = 'none';
        el.style.transform = `translateZ(-${this.radius}px) rotateY(${this.currentAngle}deg)`;
      }
      if (!e.touches) {
        this._onMouseMove = (me) => this.onDragMove(me);
        this._onMouseUp = () => this.onDragEnd();
        document.addEventListener('mousemove', this._onMouseMove);
        document.addEventListener('mouseup', this._onMouseUp);
      }
    },
    onDragMove(e) {
      if (!this._dragging) return;
      const pos = e.touches ? e.touches[0] : e;
      const dx = pos.clientX - this._dragStartX;
      if (Math.abs(dx) > 5) this._touchMoved = true;
      const angle = this._dragStartAngle + dx * 0.5;
      const el = this.$refs.carouselContent;
      if (el) {
        el.style.transform = `translateZ(-${this.radius}px) rotateY(${angle}deg)`;
      }
    },
    _clearInlineStyle() {
      const el = this.$refs.carouselContent;
      if (el) {
        el.style.transition = '';
      }
    },
    onDragEnd() {
      if (!this._dragging) return;
      this._dragging = false;
      document.removeEventListener('mousemove', this._onMouseMove);
      document.removeEventListener('mouseup', this._onMouseUp);
      if (!this._touchMoved) {
        this._clearInlineStyle();
        this._isTransitioning = false;
        return;
      }
      const el = this.$refs.carouselContent;
      let finalAngle = this.currentAngle;
      if (el) {
        const match = el.style.transform.match(/rotateY\(([-\d.]+)deg\)/);
        if (match) finalAngle = parseFloat(match[1]);
      }
      const unitAngle = 360 / this.itemCount;
      let bestIdx = 0;
      let bestTarget = 0;
      let bestDiff = Infinity;
      for (let i = 0; i < this.itemCount; i++) {
        const base = -i * unitAngle;
        const k = Math.round((finalAngle - base) / 360);
        const candidate = base + k * 360;
        const diff = Math.abs(finalAngle - candidate);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
          bestTarget = candidate;
        }
      }
      this.currentAngle = bestTarget;
      this.currentIndex = bestIdx;
      this._isTransitioning = true;
      if (el) {
        el.style.transition = 'transform 0.6s cubic-bezier(0.77, 0, 0.175, 1)';
        el.style.transform = `translateZ(-${this.radius}px) rotateY(${this.currentAngle}deg)`;
      }
      setTimeout(() => {
        this._isTransitioning = false;
        this._clearInlineStyle();
      }, 600);
    },
  },
  watch: {
    singers: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.currentAngle = 0;
          this.currentIndex = 0;
          this._isTransitioning = false;
        });
      },
    },
    itemCount() {
      this.$nextTick(() => {
        this.currentAngle = 0;
        this.currentIndex = 0;
        this._isTransitioning = false;
      });
    },
  },
  mounted() {
    this.currentAngle = 0;
    this.currentIndex = 0;
    this._isTransitioning = false;
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  },
};
</script>

<style scoped>
.singer-carousel-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 10px;
  margin-top: 0;
  width: 100%;
}

/* ================================================================
   3D 轮播图容器
   ================================================================ */
.shell {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  overflow: visible;
  flex-shrink: 0;
}

.content {
  position: relative;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
}

/* ================================================================
   轮播卡片 - 恢复为梦幻浅色背景
   ================================================================ */
.item {
  position: absolute;
  left: calc(var(--card-width) / -2);
  top: calc(var(--card-height) / -2);
  width: var(--card-width);
  height: var(--card-height);
  box-shadow: 0 8px 28px rgba(123, 147, 219, 0.35), 0 2px 8px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.3s ease;
  backface-visibility: hidden;
  overflow: visible;
  border: 3px solid var(--pixel-dark);
}

.item:hover {
  box-shadow: 0 12px 40px rgba(123, 147, 219, 0.5), 0 0 60px var(--glow-blue);
  border-color: var(--pixel-blue);
}

.card-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 10px 10px 0;
  box-sizing: border-box;
  background: linear-gradient(
    170deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 242, 255, 0.95) 45%,
    rgba(238, 243, 255, 0.92) 100%
  );
  border-radius: 6px;
  border: 2px solid var(--pixel-ice);
  position: relative;
  overflow: hidden;
}

/* 卡片顶部柔光 —— 让头像区域更梦幻 */
.card-inner::before {
  content: '';
  position: absolute;
  top: -40%;
  left: -20%;
  width: 140%;
  height: 70%;
  background: radial-gradient(ellipse at top, rgba(190, 190, 255, 0.28), transparent 70%);
  pointer-events: none;
}

/* 头像后方光圈 */
.avatar-halo {
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(var(--avatar-size) * 1.5);
  height: calc(var(--avatar-size) * 1.5);
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(196, 190, 255, 0.5), transparent 70%);
  filter: blur(4px);
  animation: haloPulse 3s ease-in-out infinite;
}

@keyframes haloPulse {
  0%, 100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.12);
  }
}

/* ================================================================
   上半部分：头像 + 猫耳 + 喇叭
   ================================================================ */
.card-top-row {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 4px;
  padding-top: 10px;
  z-index: 1;
}

.avatar-wrap {
  position: relative;
  width: var(--avatar-size);
  height: var(--avatar-size);
  flex-shrink: 0;
  z-index: 2;
  margin-top: 20px;
}

.card-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid white;
  box-shadow: 0 4px 14px rgba(123, 147, 219, 0.3), 0 0 20px rgba(196, 190, 255, 0.4);
  background: linear-gradient(135deg, var(--dream-light), var(--dream-primary), var(--dream-lavender));
  position: relative;
  z-index: 1;
}

.card-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(var(--avatar-size) * 0.45);
  color: white;
  font-family: 'ZCOOL KuaiLe', cursive;
  text-shadow: 0 3px 10px rgba(74, 90, 138, 0.6);
}

/* 猫耳 - 倾斜三角形，含内层粉色小三角 */
.cat-ear {
  position: absolute;
  width: 0;
  height: 0;
  z-index: 2;
}

.cat-ear.left {
  top: calc(var(--avatar-size) * -0.08);
  left: calc(var(--avatar-size) * -0.02);
  transform: rotate(-15deg);
  border-left: calc(var(--avatar-size) * 0.20) solid transparent;
  border-right: calc(var(--avatar-size) * 0.20) solid transparent;
  border-bottom: calc(var(--avatar-size) * 0.32) solid #d4b8ec;
  filter: drop-shadow(0 1px 4px rgba(180, 150, 220, 0.35));
}

.cat-ear.left::before {
  content: '';
  position: absolute;
  top: calc(var(--avatar-size) * 0.06);
  left: calc(var(--avatar-size) * -0.10);
  border-left: calc(var(--avatar-size) * 0.10) solid transparent;
  border-right: calc(var(--avatar-size) * 0.10) solid transparent;
  border-bottom: calc(var(--avatar-size) * 0.18) solid #f0c0d8;
}

.cat-ear.right {
  top: calc(var(--avatar-size) * -0.08);
  right: calc(var(--avatar-size) * -0.02);
  transform: rotate(15deg);
  border-left: calc(var(--avatar-size) * 0.20) solid transparent;
  border-right: calc(var(--avatar-size) * 0.20) solid transparent;
  border-bottom: calc(var(--avatar-size) * 0.32) solid #d4b8ec;
  filter: drop-shadow(0 1px 4px rgba(180, 150, 220, 0.35));
}

.cat-ear.right::before {
  content: '';
  position: absolute;
  top: calc(var(--avatar-size) * 0.06);
  left: calc(var(--avatar-size) * -0.10);
  border-left: calc(var(--avatar-size) * 0.10) solid transparent;
  border-right: calc(var(--avatar-size) * 0.10) solid transparent;
  border-bottom: calc(var(--avatar-size) * 0.18) solid #f0c0d8;
}

/* 喇叭 - 右上角，小型，不遮挡头像 */
.card-speaker {
  position: absolute;
  top: 0;
  right: 2px;
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--dream-accent);
  font-size: 8px;
  z-index: 3;
  opacity: 0.85;
  padding: 1px 3px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  backdrop-filter: blur(2px);
  text-decoration: none;
  transition: opacity 0.2s, transform 0.2s;
}

.card-speaker:hover {
  opacity: 1;
  transform: scale(1.15);
  color: var(--dream-lavender);
}

.speaker-icon {
  flex-shrink: 0;
}

.speaker-text {
  white-space: nowrap;
  font-size: 8px;
}

/* ================================================================
   名字
   ================================================================ */
.card-name {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
  z-index: 1;
}

.name-text {
  font-size: var(--name-font);
  font-weight: 700;
  color: var(--pixel-dark);
  text-shadow: 0 1px 2px rgba(139, 181, 232, 0.3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 85%;
  display: inline-block;
  padding: 0 12px;
  position: relative;
}

.name-text::before,
.name-text::after {
  content: '✦';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 8px;
  color: var(--dream-accent);
  opacity: 0.5;
}

.name-text::before {
  left: 0;
}

.name-text::after {
  right: 0;
}

/* ================================================================
   下半部分：左标签 + 右音符按钮
   ================================================================ */
.card-body {
  display: flex;
  width: 100%;
  flex: 1;
  gap: 4px;
  min-height: 0;
}

/* 左侧标签列：占一半宽度 */
.card-tags-col {
  flex: 1;
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  padding-right: 2px;
}

/* 直角梯形标签 */
.trapezoid-tag {
  font-size: var(--tag-font);
  padding: 3px 10px 3px 8px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  clip-path: polygon(0% 0%, 100% 0%, 88% 100%, 0% 100%);
  text-align: left;
  line-height: 1.5;
  border-left: 2px solid var(--dream-accent);
  font-weight: 600;
  margin-bottom: 3px;
}

.trapezoid-tag:last-child {
  margin-bottom: 0;
}

/* 流派标签 - 紫色调 */
.tag-genre {
  background: linear-gradient(135deg, rgba(196, 190, 255, 0.6), rgba(168, 184, 232, 0.4));
  border-left-color: #a78bfa;
}

/* 音色标签 - 粉色调 */
.tag-timbre {
  background: linear-gradient(135deg, rgba(255, 190, 220, 0.6), rgba(232, 168, 200, 0.4));
  border-left-color: #f472b6;
}

/* 右侧操作列：占一半宽度 */
.card-action-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
  width: 50%;
  padding-right: 6px;
}

.music-note {
  color: var(--dream-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.enter-songlist-btn {
  background: none;
  border: none;
  color: var(--dream-accent);
  font-size: var(--btn-font);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: color 0.25s ease, transform 0.25s ease;
  white-space: nowrap;
  line-height: 1;
}

.enter-songlist-btn:hover {
  color: var(--dream-lavender);
  transform: scale(1.05);
}

/* ================================================================
   导航指示器
   ================================================================ */
.carousel-indicators {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  flex-wrap: wrap;
  width: 100%;
}

.indicator-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--dream-accent);
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
}

.indicator-dot.active {
  background: linear-gradient(135deg, var(--dream-accent), var(--dream-lavender));
  border-color: var(--dream-accent);
  box-shadow: 0 0 12px var(--glow-blue);
  transform: scale(1.3);
}

.indicator-dot:hover {
  transform: scale(1.2);
  box-shadow: 0 0 8px var(--glow-blue);
}

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 768px) {
  .singer-carousel-wrapper {
    padding: 12px 0 6px;
  }
  .card-inner {
    padding: 10px 8px 8px 0;
  }
  .card-top-row {
    padding-top: 4px;
    margin-bottom: 4px;
  }
  .card-speaker {
    font-size: 6px;
    padding: 0 2px;
  }
  .speaker-text {
    font-size: 6px;
  }
  .speaker-icon {
    width: 8px;
    height: 8px;
  }
  .card-name {
    margin-bottom: 6px;
  }
  .card-tags-col {
    gap: 3px;
  }
  .trapezoid-tag {
    padding: 2px 9px 2px 7px;
  }
  .card-action-col {
    gap: 10px;
    width: 50%;
    padding-right: 4px;
  }
  .music-note svg {
    width: 22px;
    height: 22px;
  }
  .enter-songlist-btn {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .singer-carousel-wrapper {
    padding: 8px 0 4px;
  }
  .card-inner {
    padding: 8px 6px 6px 0;
  }
  .card-top-row {
    padding-top: 3px;
    margin-bottom: 3px;
  }
  .card-speaker {
    font-size: 5px;
    padding: 0 1px;
  }
  .speaker-text {
    font-size: 5px;
  }
  .speaker-icon {
    width: 7px;
    height: 7px;
  }
  .card-name {
    margin-bottom: 5px;
  }
  .card-tags-col {
    gap: 2px;
  }
  .trapezoid-tag {
    padding: 2px 8px 2px 6px;
    font-size: 9px;
  }
  .card-action-col {
    gap: 8px;
    width: 50%;
    padding-right: 2px;
  }
  .music-note svg {
    width: 18px;
    height: 18px;
  }
  .enter-songlist-btn {
    font-size: 9px;
  }
}
</style>